
import { useState, useEffect, useCallback, useRef } from 'react';
import { useNotificationsFetcher } from './notifications/useNotificationsFetcher';
import { useNotificationCounter } from './notifications/useNotificationCounter';
import { useNotificationActions } from './notifications/useNotificationActions';
import { useRealtimeNotifications } from './notifications/useRealtimeNotifications';
import { useAuth } from '@/contexts/auth';
import { toast } from 'sonner';

export const useNotifications = () => {
  const { user } = useAuth();
  const [error, setError] = useState<string | null>(null);
  const [isLoading, setLoading] = useState(false);
  const autoRefreshTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  
  const { 
    notifications, 
    loading: fetcherLoading, 
    fetchNotifications, 
    setNotifications, 
    purgeIncorrectNotificationsForCurrentUser, 
    deepPurgeNotifications 
  } = useNotificationsFetcher();
  
  const { unreadCount, setUnreadCount } = useNotificationCounter(notifications);
  
  const { markAsRead, markAllAsRead } = useNotificationActions({ 
    notifications, 
    setNotifications, 
    setUnreadCount 
  });
  
  useRealtimeNotifications({
    setNotifications,
    setUnreadCount,
    fetchNotifications
  });

  // Function to refresh notifications
  const refreshNotifications = useCallback(async (silent: boolean = false) => {
    if (!user?.id) return [];
    
    if (!silent) {
      setLoading(true);
    }
    
    setError(null);
    
    try {
      console.log(`Refreshing notifications for user: ${user.id} (silent: ${silent})`);
      const refreshedNotifications = await fetchNotifications();
      console.log(`Refresh complete, found ${refreshedNotifications.length} notifications`);
      return refreshedNotifications;
    } catch (error) {
      console.error('Error refreshing notifications:', error);
      setError('Could not load notifications. Please try again.');
      if (!silent) {
        toast.error('Could not refresh notifications');
      }
      return [];
    } finally {
      if (!silent) {
        setLoading(false);
      }
    }
  }, [user?.id, fetchNotifications]);

  // This function handles deep purging with improved error handling
  const handleDeepPurge = useCallback(async () => {
    if (!user?.id) return false;
    
    try {
      setLoading(true);
      setError(null);
      
      // Attempt the deep purge with better error handling
      const success = await deepPurgeNotifications();
      
      if (success) {
        // After successful purge, refresh the notifications list to ensure UI is updated
        await fetchNotifications();
        
        // Update unread count to 0 since we've purged all notifications
        setUnreadCount(0);
        
        toast.success('Notifications reset successfully');
        return true;
      } else {
        throw new Error('Reset operation did not complete successfully');
      }
    } catch (error: any) {
      console.error('Error in deep purge:', error);
      setError('Could not reset notifications. Please try again.');
      toast.error('Could not reset notifications. Please try again.');
      return false;
    } finally {
      setLoading(false);
    }
  }, [user?.id, deepPurgeNotifications, fetchNotifications, setUnreadCount]);

  return { 
    notifications, 
    unreadCount, 
    loading: isLoading || fetcherLoading, 
    error,
    markAsRead, 
    markAllAsRead, 
    fetchNotifications,
    refreshNotifications,
    purgeIncorrectNotificationsForCurrentUser,
    deepPurgeNotifications: handleDeepPurge
  };
};
